import { Component, ChangeDetectionStrategy, OnInit, Inject } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { select, Store } from '@ngrx/store';
import { AppState } from '@root/app-state.module';
import {
  selectEntitledStates,
  selectIsPinMember,
  selectProvisionedGeos
} from '@store/user/user.selector';
import { Observable } from 'rxjs';
import { Region, StateInfo } from '@store/user/user.model';
import { take, tap, withLatestFrom } from 'rxjs/operators';
import {
  Attribute,
  AttributesCategory
} from '@shared/components/categories-attributes-selector/categories-attributes-selector.model';
import { StringUtils } from '@shared/utils/string-utils';
import { SelectionPropertiesItem } from '@store/search-board/search-board.model';
import { SaveRegionsAction } from '@store/user/user.actions';
import { selectMaxNumberCountiesSelection } from '@store/search-board/search-board.selector';
import { MixpanelService } from '@shared/services/mixpanel.service';
import { FormControl, UntypedFormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'rlst-change-region',
  templateUrl: './change-region.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChangeRegionComponent implements OnInit {
  public counties: Array<Region> = [];
  public displayedCounties: Array<Attribute>;

  public selectedRegions: Array<Region> = [];
  public selectedAttributes: Array<SelectionPropertiesItem> = [];

  public selectedState: AttributesCategory = null;
  public statesList: Array<StateInfo>;
  public displayedStates: Array<AttributesCategory>;

  public maxNumberCountiesSelection$: Observable<number>;
  public isMobileNextStep: boolean = false;
  public headerTitle: string = 'Selected Counties';
  public nextStepButtonLabel: string = 'Select Counties';
  public aggregateCategoryTitle: string = 'All States';
  public countyFilterControl: UntypedFormControl;
  public isMarketTrendsOnly: boolean;

  constructor(private readonly dialogRef: MatDialogRef<ChangeRegionComponent>,
              private readonly store$: Store<AppState>,
              private readonly mixpanelService: MixpanelService,
              @Inject(MAT_DIALOG_DATA) public data: { showFilter: boolean }
            ) {}

  ngOnInit() {
    this.store$.pipe(
      take(1),
      select(selectEntitledStates)
    ).subscribe((states: Array<StateInfo>) => {
      this.displayedStates = this.mapStatesToCategories(states);
      this.statesList = states;
      if (this.displayedStates.length === 1) {
        this.aggregateCategoryTitle = '';
        this.selectedState = this.displayedStates[0];
      }
    });
    this.store$.pipe(
      take(1),
      select(selectProvisionedGeos),
      withLatestFrom(this.store$.pipe(select(selectIsPinMember))),
      tap(([counties, isPinMember]: [Array<Region>, boolean]) => {
        if (this.data.showFilter) {
          this.isMarketTrendsOnly = !isPinMember || !counties.some((county: Region) => county.isListingTrendCounty);
        }
      })
    ).subscribe(([counties, _]: [Array<Region>, boolean]) => {
            this.counties = counties.map((county: Region) => ({...county} as Region));
      this.displayedCounties = this.mapCountiesToAttributes(counties, true);
    });
    this.maxNumberCountiesSelection$ = this.store$.pipe(
      take(1),
      select(selectMaxNumberCountiesSelection)
    );
    this.countyFilterControl = new FormControl("ALL");

    this.countyFilterControl.valueChanges.pipe(untilDestroyed(this))
      .subscribe(() => this.selectState(this.selectedState));
  }

  public removeRegion(selection: SelectionPropertiesItem): void {
    const changedCountyAttribute: Attribute = this.displayedCounties.find((county: Attribute) => county.id === selection.id);
    if (changedCountyAttribute) {
      changedCountyAttribute.selected = false;
    }
    this.displayedCounties = [...this.displayedCounties];
    const changedCounty: Region = this.counties.find((county: Region) => county.fips === selection.id);
    if (changedCounty) {
      changedCounty.selected = false;
      const indexToRemove = this.selectedRegions.findIndex((county: Region) => county.fips === selection.id);
      this.selectedRegions.splice(indexToRemove, 1);
      this.selectedAttributes.splice(indexToRemove, 1);
      this.selectedAttributes = [...this.selectedAttributes];
    }
  }

  public isBothCountiesSelected(): boolean {
    let countyTypes: Array<boolean>;
    if(this.selectedRegions.length > 1) {
      countyTypes = this.selectedRegions.map((region: Region) => region.isListingTrendCounty);
    }
    return countyTypes?.includes(true) && countyTypes?.includes(false);
  }

  public onSelectionOrderChanged(): void {
    this.selectedRegions = this.selectedAttributes.map((attribute: SelectionPropertiesItem) => {
      return this.selectedRegions.find((region: Region) => region.fips === attribute.id);
    });
  }

  public clearSelection(): void {
    this.counties.forEach((county: Region) => county.selected = false);
    this.displayedCounties.forEach((county: Attribute) => county.selected = false);
    this.displayedCounties = [...this.displayedCounties];
    this.selectedRegions = [];
    this.selectedAttributes = [];
  }

  public toggleMobileNextStep(): void {
    this.isMobileNextStep = !this.isMobileNextStep;
  }

  public backButtonClickHandler(): void {
    if (this.isMobileNextStep) {
      this.isMobileNextStep = !this.isMobileNextStep;
    } else {
      this.close();
    }
  }

  public selectState(state: AttributesCategory): void {
    this.selectedState = state;
    let selectedStateObj: StateInfo;
    if (state) {
      selectedStateObj = this.statesList.find((stateInfo: StateInfo) => {
        return stateInfo.stateName.toLowerCase() === state.name.toLowerCase();
      });
    }
    if (selectedStateObj) {
      this.displayedCounties = this.mapCountiesToAttributes(
        this.counties.filter((county: Region) => county.stateCode === selectedStateObj.stateCode)
      );
    } else {
      this.displayedCounties = this.mapCountiesToAttributes(this.counties);
    }

    if (this.countyFilterControl.value !== 'ALL') {
      const isListingTrendSelected: boolean = this.countyFilterControl.value === 'LT';
      this.displayedCounties = this.displayedCounties.filter((county: Attribute) => county['isListingTrendCounty'] === isListingTrendSelected);
    }
  }

  public selectAttribute(attribute: Attribute): void {
    const changedCountyAttribute: Attribute = this.displayedCounties.find((county: Attribute) => county.id === attribute.id);
    if (changedCountyAttribute) {
      changedCountyAttribute.selected = attribute.selected;
    }
    const changedCounty: Region = this.counties.find((county: Region) => county.fips === attribute.id);
    if (changedCounty) {
      changedCounty.selected = attribute.selected;
      if (attribute.selected) {
        this.selectedRegions.push(changedCounty);
        this.selectedAttributes.push({
          ...changedCounty,
          id: changedCounty.fips,
          tooltip: `${changedCounty.countyName}, ${changedCounty.stateCode}`
        });
      } else {
        const indexToRemove = this.selectedRegions.findIndex((county: Region) => county.fips === attribute.id);
        this.selectedRegions.splice(indexToRemove, 1);
        this.selectedAttributes.splice(indexToRemove, 1);
      }
      this.selectedAttributes = [...this.selectedAttributes];
    }
  }

  public apply(): void {
    this.mixpanelService.track('Search - County Option', {
      countyList: this.selectedRegions.map((x) => x.fips)
    });
    this.store$.dispatch(new SaveRegionsAction(this.selectedRegions));
    this.close();
  }

  public close(): void {
    this.dialogRef.close();
    this.isMobileNextStep = false;
  }

  private mapCountiesToAttributes(counties: Array<Region>, setSelectedCounties: boolean = false): Array<Attribute> {
    if (setSelectedCounties) {
      this.selectedRegions = [];
      this.selectedAttributes = [];
    }
    return counties.map((county: Region) => {
      if (setSelectedCounties && county.selected) {
        this.selectedRegions.push(county);
        this.selectedAttributes.push({
          ...county,
          id: county.fips,
          tooltip: `${county.countyName}, ${county.stateCode}`
        });
      }
      return  { name: county.countyName,
         label: `${county.countyName}, ${county.stateCode}`,
         selected: county.selected,
         id: county.fips,
         isListingTrendCounty: county.isListingTrendCounty,
      } as Attribute;
    });
  }

  private mapStatesToCategories(states: Array<StateInfo>): Array<AttributesCategory> {
    return states.map((state: StateInfo) => {
      return {
        name: StringUtils.capitalizeFirstLetterInEveryWord(state.stateName)
      } as AttributesCategory;
    });
  }
}
